import { useEffect, useState, useRef } from 'react';
import Header from '../Header/Header';
import MapPicker from '../MapPicker/MapPicker';
import { resolveDomestic } from '../../utils/regionState';
import Toast from '../Toast/Toast';
import HourlyForecast from './HourlyForecast';
import WeeklyForecast from './WeeklyForecast';
import LocationPopup from './LocationPopup';
import useLocation from '../../hooks/useLocation';
import useLocationList from '../../hooks/useLocationList';
import { getShortWeather, getDailyForecast } from '../../api/weatherApi';
import { getAddress } from '../../api/addressApi';
import { processHourlyData, processWeeklyFromHourly, processDailyData } from '../../utils/weatherTransform';
import { TodayWeatherContainer } from './TodayWeather.styles';

const TodayWeather = () => {
    const [todayData, setTodayData] = useState(null);
    const [weekData, setWeekData] = useState([{}, {}, {}, {}, {}, {}, {}]);
    const [week3Data, setWeek3Data] = useState(new Array(4));
    const [week4Data, setWeek4Data] = useState(new Array(3));
    const [location, setLocation] = useState(null);
    const tempRange = useRef(null);

    const [showPopup, setShowPopup] = useState(false);
    const [isShowMap, setShowMap] = useState(false);
    const [mapLat, setMapLat] = useState(0);
    const [mapLon, setMapLon] = useState(0);
    const [toastMsg, setToastMsg] = useState(null);

    const { loading: loadingPosition, getLocation, cachedLat, cachedLon } = useLocation();
    const {
        locationList, selectedLocation,
        load: loadLocationList, addLocation, removeLocation, selectLocation
    } = useLocationList();

    const init = () => {
        setTodayData(null);
        setWeekData([{}, {}, {}, {}, {}, {}, {}]);
        setWeek3Data(new Array(4));
        setWeek4Data(new Array(3));
        tempRange.current = null;
    };

    const getGeolocation = () => {
        if (cachedLat.current && cachedLon.current) {
            setLocation({ lat: cachedLat.current, lot: cachedLon.current });
        } else {
            setToastMsg("위치 정보 확인 중...");
            getLocation(
                ({ lat, lon }) => {
                    setToastMsg(null);
                    setLocation({ lat, lot: lon });
                },
                () => setToastMsg("위치 정보를 가져올 수 없습니다")
            );
        }
    };

    // 단기예보 (24시간 + 4일)
    const fetchShortWeather = () => {
        setToastMsg("날씨 정보 조회 중...");
        getShortWeather({ lat: location.lat, lng: location.lot }, (timeData) => {
            setToastMsg(null);
            let { todayDataArr, tempRange: tr } = processHourlyData(timeData);
            tempRange.current = tr;
            setTodayData(todayDataArr);
            setWeek3Data(processWeeklyFromHourly(timeData));
        });
    };

    // 중기예보 (4~7일)
    const fetchDailyForecast = () => {
        getDailyForecast({ lat: location.lat, lng: location.lot }, (data) => {
            setWeek4Data(processDailyData(data));
        });
    };

    // 위치 변경 시 날씨 다시 조회
    useEffect(() => {
        init();
        if (location) {
            fetchShortWeather();
            fetchDailyForecast();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    // 주간 데이터 합성 (4일 단기 + 3일 중기)
    useEffect(() => {
        let data = [];
        for (let i = 0; i < 7; i++) {
            if (i < 4) data.push(week3Data[i] || {});
            else data.push(week4Data[i - 4] || {});
        }
        setWeekData(data);
    }, [week3Data, week4Data]);

    // 초기 로드
    useEffect(() => {
        init();
        window.gtag("event", "Page_오늘");

        let info = loadLocationList();
        if (info) {
            if (info.selected === "") {
                getGeolocation();
            } else {
                selectedLocation.current = info.selected;
            }
            if (info.list) {
                let selected = info.list.find(item => item.key === info.selected);
                if (selected) {
                    setLocation({ lat: selected.lat, lot: selected.lot });
                }
            }
        } else {
            getGeolocation();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const locationChange = (e) => {
        if (e.target.value === "") {
            selectLocation("");
            getGeolocation();
        } else {
            let data = locationList[e.target.selectedIndex - 1];
            selectLocation(data.key);
            setLocation({ lat: data.lat, lot: data.lot });
        }
    };

    const openMapClick = () => {
        getLocation(
            ({ lat, lon }) => {
                setMapLat(lat);
                setMapLon(lon);
                setShowMap(true);
            },
            () => setToastMsg("위치 정보를 가져올 수 없습니다")
        );
    };

    const handleMapSelect = (lat, lon) => {
        getAddress(lon, lat, (data) => {
            addLocation({ label: data.address_name, lat, lot: lon });
        });
        setShowMap(false);
    };

    const delClick = (idx) => {
        let { isReload } = removeLocation(idx);
        if (isReload) {
            init();
            getGeolocation();
        }
    };

    return (
        <TodayWeatherContainer>
            <Header />
            <label className='scdFont titleLb'>오늘</label><br /><br />

            <div className='locationRow'>
                <select className='locationSelect' onChange={locationChange} value={selectedLocation.current}>
                    <option value="">현재 위치</option>
                    {locationList && locationList.map((item, i) => (
                        <option key={item.label + i} value={item.key}>{item.label}</option>
                    ))}
                </select>
                <button className='settingBtn' onClick={() => setShowPopup(true)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="#495057" strokeWidth="1.8"/>
                        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" stroke="#495057" strokeWidth="1.8"/>
                    </svg>
                </button>
            </div>

            <HourlyForecast todayData={todayData} tempRange={tempRange.current} />
            <WeeklyForecast weekData={weekData} />

            {showPopup &&
                <LocationPopup
                    locationList={locationList}
                    onDelete={delClick}
                    onOpenMap={openMapClick}
                    onClose={() => setShowPopup(false)}
                />
            }

            <MapPicker
                show={isShowMap}
                lat={mapLat}
                lon={mapLon}
                provider={resolveDomestic(mapLat, mapLon) ? 'kakao' : 'google'}
                onSelect={handleMapSelect}
                onClose={() => setShowMap(false)}
            />

            {loadingPosition &&
                <div className='loadingMap'>
                    <span>위치 확인 중..</span>
                </div>
            }
            <Toast message={toastMsg} />
        </TodayWeatherContainer>
    );
};

export default TodayWeather;
